// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/Announcements.js

import React, { useState } from 'react';
import { FiBell as BellIcon, FiChevronRight as ChevronRight, FiX } from 'react-icons/fi';
import useBrandColors from '../utils/useBrandColors';

const Announcements = ({ announcements = {} }) => {
  const [isVisible, setIsVisible] = useState(true);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  
  const primaryAnnouncement = announcements?.primary || {};
  const secondaryAnnouncements = announcements?.secondary || [];
  const allAnnouncements = [];

  // Collect all active announcements
  if (primaryAnnouncement?.title && primaryAnnouncement?.isActive !== false) {
    allAnnouncements.push({ ...primaryAnnouncement, isPrimary: true });
  }
  
  secondaryAnnouncements.forEach(item => {
    if (item.isActive !== false && item.title) {
      allAnnouncements.push({ ...item, isPrimary: false });
    }
  });

  if (!isVisible || allAnnouncements.length === 0) return null;

  const getTypeIcon = (type) => {
    const icons = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      danger: '🚫',
    };
    return icons[type] || '📢';
  };

  return (
    <div 
      id="announcements"
      className="relative z-20 border-y shadow-sm overflow-hidden"
      style={{
        backgroundColor: '#ffffff',
        borderColor: `${primary}15`,
        fontFamily: fontBody,
      }}
    >
      {/* CSS Keyframes injected dynamically for marquee smooth scrolling */}
      <style>{`
        @keyframes marquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .animate-marquee-continuous {
          display: flex;
          width: max-content;
          animation: marquee 30s linear infinite;
        }
        .animate-marquee-continuous:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="flex items-center">
        {/* Left Fixed Badge */}
        <div 
          className="flex items-center gap-2 px-5 py-3 font-bold text-xs uppercase tracking-wider relative z-30 shadow-md flex-shrink-0 text-white"
          style={{ 
            backgroundColor: primary,
            fontFamily: fontHeading 
          }}
        >
          <BellIcon className="w-4 h-4 animate-bounce" style={{ color: secondary }} />
          <span>Updates</span>
        </div>

        {/* Marquee Body Window */}
        <div className="flex-1 overflow-hidden relative py-3 bg-gray-50/50">
          {/* Double up elements to create flawless endless wrap-around look */}
          <div className="animate-marquee-continuous whitespace-nowrap flex items-center">
            {[...allAnnouncements, ...allAnnouncements].map((announcement, index) => (
              <div 
                key={index} 
                className="inline-flex items-center gap-3 mx-12 text-sm"
              >
                <span className="text-base flex-shrink-0">
                  {announcement.isPrimary ? '🔥' : getTypeIcon(announcement.type)}
                </span>
                
                <span 
                  className={`font-semibold ${announcement.isPrimary ? 'underline decoration-2' : 'text-gray-900'}`}
                  style={{ 
                    fontFamily: fontHeading,
                    clear: 'none',
                    color: announcement.isPrimary ? primary : 'inherit'
                  }}
                >
                  {announcement.title}
                </span>

                {announcement.content && (
                  <span className="text-gray-600 font-normal">
                    — {announcement.content}
                  </span>
                )}

                {announcement.link && (
                  <a
                    href={announcement.link}
                    className="inline-flex items-center gap-0.5 text-xs font-bold transition-all hover:underline ml-1"
                    style={{ color: secondary }}
                  >
                    <span>View Details</span>
                    <ChevronRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Dismiss Button */}
        <div className="px-4 bg-gray-50/50 py-3 flex-shrink-0 border-l border-gray-200/60 z-30">
          <button
            onClick={() => setIsVisible(false)}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 transition-all"
            aria-label="Dismiss Announcements"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Announcements;